import {HttpClient, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {ModuleWithProviders, NgModule, isDevMode} from '@angular/core';
import {AuthConfigService} from '@app/auth-config/services/auth-config.service';
import {AuthenticatorSetupActions} from '@app/mfa-setup/state/authenticator-setup.actions';
import {MfaSetupEffects} from '@app/mfa-setup/state/authenticator-setup.effects';
import {ELanguage} from '@app/shared/enums/language.enum';
import {LanguageService} from '@app/shared/services/language.service';
import {DynamicFormEffects} from '@app/shared/state/dynamic-form/dynamic-form.effects';
import {HeaderEffects} from '@app/shared/state/header/header.effects';
import {LanguageEffects} from '@app/shared/state/language/language.effects';
import {environment} from '@environments/environment';
import {EffectsModule} from '@ngrx/effects';
import {Action, Store, StoreModule} from '@ngrx/store';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {State, rootReducer} from '../state/reducers';
import {LanguageLoader} from './Loader/language.loader';
import {CryptoService} from './services/crypto.service';
import {LocalStorageService} from './services/local-storage.service';
import {FeatureFlagEffects} from './state/feature-flag.effects';
import {PlatformStorage} from './tokens/platform-storage.token';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

const sanitizeMfaAction = (action: Action): Action & {response?: string; code?: string} => {
  switch (action.type) {
    case AuthenticatorSetupActions.LoadAuthenticatorSetupSuccess:
    case AuthenticatorSetupActions.VerifyAuthenticatorSetupSuccess:
      return {...action, response: '[REDACTED]'};
    case AuthenticatorSetupActions.VerifyAuthenticatorSetup:
      return {...action, code: '[REDACTED]'};
    default:
      return action;
  }
};

const sanitizeMfaState = (state: State): State => ({
  ...state,
  mfaSetupState: {
    ...state.mfaSetupState,
    response: null,
    verificationResponse: null,
  },
});

@NgModule({
  declarations: [],
  exports: [TranslateModule],
  imports: [
    TranslateModule.forRoot({
      defaultLanguage: `${ELanguage.EN}`,
      loader: {
        provide: TranslateLoader,
        useClass: LanguageLoader,
        deps: [LanguageService, Store, AuthConfigService, PlatformStorage, OidcSecurityService],
      },
    }),
    EffectsModule.forRoot([HeaderEffects, LanguageEffects, DynamicFormEffects, FeatureFlagEffects, MfaSetupEffects]),
    StoreModule.forRoot(rootReducer),
    ...(!environment.production
      ? [
          StoreDevtoolsModule.instrument({
            maxAge: 50,
            logOnly: !isDevMode(),
            connectInZone: true,
            actionSanitizer: sanitizeMfaAction,
            stateSanitizer: sanitizeMfaState,
          }),
        ]
      : []),
  ],
  providers: [provideHttpClient(withInterceptorsFromDi())],
})
export class CoreModule {
  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        {
          provide: PlatformStorage,
          useFactory: () => {
            return new LocalStorageService();
          },
          deps: [CryptoService],
        },
      ],
    };
  }
}
